/**
 * @author    sam@zumper.com (Sam Sherman)
 * @copyright Copyright (c) 2018, Zumper
 * @description Columnar JSON encode/decode
 */

import polyline from '@mapbox/polyline'

/**
 * serialize lists of objects in columnar json fashion
 * [{foo: 1}, {foo: 2}, {foo: 3}, ..] => {foo: [1, 2, 3, ..], '#': n}
 
 * built-in support for a variety of type-as-column encodings
 # default:      proceed as tho there were no spec, columnar-izing recursively
 # list:         identity mapping. useful when heterogeneous objects are in the same list
 #               [{a: 1}, {b: 2}] => [{a: 1}, {b: 2}]
 * polyline:     lat, lng pairs as a string
 *               [(11.1, 100.1), (11.2, 100), (11.3, 100.2)] => '_~vbA_x}aR_pR~oR_pR_af@'
 * delta:        delta encoded list of numbers.
 *               [100, 101, 102, 103] => [100, 1, 1, 1]
 * true:         positional indices indicate true, missing is assumed false. cannot use with nulls
 *               [true, false, true, false] => [4, 0, 2]
 * false:        positional indices indicate false, missing is assumed true. cannot use with nulls
 *               [true, false, true, false] => [4, 1, 3]
 * rle:          run length encoded, produces inline pairs length/value pairs.
 *               ['abc', 'abc', 'abc', 'def'] => [3, 'abc', 1, 'def']
 *               [100, 100, 100, 200] => [3, 100, 1, 200]
 * binomial:     values are paired with delta-encoded positions,
 *               eg. for binomial distribution of tags etc.
 *               assumed to not be run-length-effective due to low likelihood of runs
 *               ['cat', 'dog', 'cat', 'cat', 'dog'] = ['cat', [0, 2, 1], 'dog', [1, 3]]
 */
const DEFAULT = 'default'
const LIST = 'list'
const POLYLINE = 'polyline'
const DELTA = 'delta'
const TRUE = 'true'
const FALSE = 'false'
const RLE = 'rle'
const BINOMIAL = 'binomial'

const identity = (x) => x
const kvpValue = (k, v) => v
const keyOf = (o, k) => {
  const v = o && o[k]
  return v !== null && v !== undefined ? v : null
}

/**
 * encodeDelta([10, 11, 12, 20]) -> [10, 1, 1, 8]
 *
 * @param {int[]} items
 * @param {int[]} result - if provided, encode into this result array
 * @param {number} offset - if provided encode into the result array at this offset
 * @returns {int[]}
 */
export const encodeDelta = (items, result = null, offset = 0) => {
  if (items && items.length) {
    let prior = 0
    const results = result || new Array(items.length)
    for (let i = 0, len = items.length; i < len; ++i) {
      const item = items[i]
      results[offset + i] = item - prior
      prior = item
    }
    return results
  }
  return result || items
}

/**
 * decodeDelta([10, 1, 1, 8]) -> [10, 11, 12, 20]
 *
 * @param {int[]} data
 * @param {int[]} result - if provided, decode into this result array
 * @param {number} offset - if provided decode into the result array at this offset
 * @returns {int[]}
 */
export const decodeDelta = (data, result = null, offset = 0) => {
  if (data && data.length) {
    let total = 0
    const results = result || new Array(data.length)
    for (let i = 0, len = data.length; i < len; ++i) {
      total += data[i]
      results[offset + i] = total
    }
    return results
  }
  return result || data
}

/**
 * encode "binomial" encodeBinomial(['a', 'b', 'a', 'b']) -> ['a', [0, 2], 'b', [1, 2]]
 * items must be 'hashable' to use this encoding, eg. cannot be lists etc.
 *
 * @param {Array} items
 * @returns {Array}
 */
export const encodeBinomial = (items) => {
  if (items && items.length) {
    // [x for pair in [(v, to_deltas(p)) for v, p in positions.iteritems()] for x in pair]
    const keyed = {}
    const indexed = []
    for (let i = 0, len = items.length; i < len; ++i) {
      const v = items[i]
      const info = keyed[v]
      if (info) {
        info[1].push(i)
      } else {
        indexed.push((keyed[v] = [v, [i]]))
      }
    }
    const result = new Array(2 * indexed.length)
    for (let i = 0, pos = 0, len = indexed.length; i < len; ++i, pos += 2) {
      const [item, indexes] = indexed[i]
      result[pos] = item
      result[pos + 1] = encodeDelta(indexes, indexes)
    }
    return result
  }
  return items
}

/**
 * decode "binomial" decodeBinomial(['a', [0, 2], 'b', [1, 2]]) -> ['a', 'b', 'a', 'b']
 * data must be 'hashable' to use this encoding, eg. cannot be lists etc.
 *
 * @param {Array} data
 * @returns {Array}
 */
export const decodeBinomial = (data) => {
  if (data && data.length) {
    let total = 0
    for (let i = 1, len = data.length; i < len; i += 2) {
      total += data[i].length
    }
    const result = new Array(total)
    for (let i = 0, len = data.length; i < len; i += 2) {
      const item = data[i]
      const indexes = decodeDelta(data[i + 1])
      for (let j = 0, len2 = indexes.length; j < len2; ++j) {
        result[indexes[j]] = item
      }
    }
    return result
  }
  return data
}

/**
 * encode run-length-encoding:: encodeRle(['a', 'b', 'b', 'b']) -> [1, 'a', 3, 'b']
 *
 * @param {Array} items
 * @returns {Array}
 */
export const encodeRle = (items) => {
  if (items && items.length) {
    let count = 0
    let prior = items[0]
    const encoded = []
    for (let i = 0, len = items.length; i < len; ++i) {
      const item = items[i]
      if (item === prior) {
        ++count
      } else {
        encoded.push(count)
        encoded.push(prior)
        count = 1
        prior = item
      }
    }
    encoded.push(count)
    encoded.push(prior)
    return encoded
  }
  return items
}

/**
 * encode the 'true' encoding:: encodeTrue([False, True, True, False]) -> [4, 1, 1]
 * - length 4, indexes 1, 2
 * - indexes are delta encoded
 *
 * @param {Array.<boolean>} items
 * @returns {int[]}
 */
export const encodeTrue = (items) => {
  if (items && items.length) {
    const indexes = []
    for (let i = 0, len = items.length; i < len; ++i) {
      if (items[i]) {
        indexes.push(i)
      }
    }
    const result = new Array(1 + indexes.length)
    result[0] = items.length
    return encodeDelta(indexes, result, 1)
  }
  return items
}

/**
 * encode the 'false' encoding:: encodeFalse([False, True, True, False]) -> [4, 0, 3]
 * - length 4, indexes 0, 3
 * - indexes are delta encoded
 *
 * @param {bool[]} items
 * @returns {int[]}
 */
export const encodeFalse = (items) => {
  if (items && items.length) {
    const indexes = []
    for (let i = 0, len = items.length; i < len; ++i) {
      if (items[i] !== null && !items[i]) {
        indexes.push(i)
      }
    }
    const result = new Array(1 + indexes.length)
    result[0] = items.length
    return encodeDelta(indexes, result, 1)
  }
  return items
}

/**
 * base implementation for decodeTrue/decodeFalse
 *
 * @param data
 * @param {boolean} bool
 * @returns {Array.<boolean>}
 */
const decodeBool = (data, bool) => {
  if (data && data.length) {
    let pos = 0
    const decoded = new Array(data[0]).fill(!bool)
    // iterate over the delta encoded positions, assigning value at the accumulated positions
    for (let i = 1, len = data.length; i < len; ++i) {
      pos += data[i]
      decoded[pos] = bool
    }
    return decoded
  }
  return data
}

/**
 * decode the 'true' encoding:: decodeTrue([4, 1, 1]) -> [False, True, True, False]
 *
 * @param {int[]} data
 * @returns {Array.<boolean>}
 */
export const decodeTrue = (data) => {
  return decodeBool(data, true)
}

/**
 * decode the 'false' encoding:: decodeFalse([4, 0, 3]) -> [False, True, True, False]
 *
 * @param {int[]} data
 * @returns {Array.<boolean>}
 */
export const decodeFalse = (data) => {
  return decodeBool(data, false)
}

/**
 * decode run-length-encoding:: decodeRle([1, 'a', 3, 'b']) -> ['a', 'b', 'b', 'b']
 *
 * @param {Array} data
 * @returns {Array}
 */
export const decodeRle = (data) => {
  let decodedLen = 0
  for (let i = 0; i < data.length; i += 2) {
    decodedLen += data[i]
  }
  const decoded = new Array(decodedLen)
  let next = 0
  for (let i = 0; i < data.length; i += 2) {
    const count = data[i]
    const item = data[i + 1]
    decoded.fill(item, next, next + count)
    next += count
  }
  return decoded
}

export const encodePolyline = polyline.encode
export const decodePolyline = polyline.decode

export const ENCODINGS = {
  [DEFAULT]: { spec: DEFAULT, encode: null, decode: null },
  [LIST]: { spec: LIST, encode: identity, decode: identity },
  [POLYLINE]: { spec: POLYLINE, encode: encodePolyline, decode: decodePolyline },
  [DELTA]: { spec: DELTA, encode: encodeDelta, decode: decodeDelta },
  [TRUE]: { spec: TRUE, encode: encodeTrue, decode: decodeTrue },
  [FALSE]: { spec: FALSE, encode: encodeFalse, decode: decodeFalse },
  [RLE]: { spec: RLE, encode: encodeRle, decode: decodeRle },
  [BINOMIAL]: { spec: BINOMIAL, encode: encodeBinomial, decode: decodeBinomial },
}

/**
 * encode the list of items into the 'dense columnar format' (a dict)
 * gets values from each item per column converts and moves to next column
 *
 * @param {* | Array | object} items
 * @param {object | undefined} spec - describes customized conversions, if undefined always use 'list'
 * @param {Record<string, object> | undefined} encodings - built-in encode/decode pairs
 * @returns {*}
 */
export const encode = (items, spec = null, encodings = null) => {
  if (!items) {
    return items
  }
  if (spec) {
    const encs = encodings || ENCODINGS
    if (typeof spec === 'object' || Array.isArray(spec)) {
      return isKeyed(items) ? encodeItem(items, spec, encs) : encodeArray(items, spec, encs)
    }
    return condenseList(encoder(getTypeDef(spec, encs, spec), encs)(items))
  }

  let keyed = isKeyed(items)
  if (keyed === null) {
    // 1. scalar
    return items
  }
  if (keyed === false) {
    // 2 or 3 or 5
    keyed = isKeyed(items.find((x) => x !== null))

    if (keyed === null) {
      // 2. list[scalar]
      return items
    }
    if (keyed === false) {
      // 3. list[list]
      // cannot use items.map() b/c it will provide the index as the spec arg
      const result = new Array(items.length)
      for (let i = 0, len = items.length; i < len; ++i) {
        result[i] = encode(items[i])
      }
      return result
    }
    // 5. list[dict|object]
    const keys = keysOfItems(items)
    const result = { '#': items.length }
    for (let i = 0, len = keys.length; i < len; ++i) {
      const key = keys[i]
      result[key] = encode(gatherValues(key, items))
    }
    return result
  }

  // 4. dict|object
  const encoded = {}
  const keys = Object.keys(items)
  for (let i = 0, len = keys.length; i < len; ++i) {
    const key = keys[i]
    const val = items[key]
    encoded[key] = val && typeof val === 'object' ? encode(val) : val
  }
  return encoded
}

const isKeyed = (value) => {
  return value && typeof value === 'object' ? !Array.isArray(value) : null
}

const condenseList = (value) => {
  return value && value.length && Array.isArray(value) && value.every((v) => v === null)
    ? null
    : value
}

// handle encoding a list of objects with a spec
const encodeArray = (items, spec, encodings) => {
  const n = items.length
  if (n) {
    const result = { '#': n }
    const keys = Object.keys(spec)
    for (let i = 0, len = keys.length; i < len; ++i) {
      const key = keys[i]
      result[key] = encodeColumn(items, key, spec[key], spec, encodings)
    }
    return result
  }
  return []
}

// handle encoding a single object with a spec
const encodeItem = (item, spec, encodings) => {
  const result = {}
  const keys = Object.keys(spec)
  for (let i = 0, len = keys.length; i < len; ++i) {
    const key = keys[i]
    const typeDef = getTypeDef(spec[key], encodings, spec)
    const { isList, isObject, attributes, encoding } = typeDef
    if (attributes) {
      // a scalar with attribute gathering
      result[key] = condenseList(gatherKeys(attributes, item))
    } else {
      const raw = keyOf(item, key)
      let value = null
      if (raw === null || raw === undefined) {
        value = null
      } else if (!raw) {
        value = raw
      } else if (isList) {
        // 5. list[dict|object]
        // encode the list field according to the sub-encoding
        value =
          typeof encoding === 'object' && !Array.isArray(encoding)
            ? encodeArray(raw, encoding, encodings)
            : encodeColumn(raw, key, encoding, spec, encodings, kvpValue)
      } else if (isObject) {
        // 4. dict|object
        value = encodeItem(raw, encoding, encodings)
      } else {
        // a scalar, just carry the value unchanged..
        value = condenseList(raw)
      }
      if (value !== null) {
        result[key] = value
      }
    }
  }
  return result
}

/**
 * decode a list of items from the 'dense columnar format'
 *
 * @param {*} data
 * @param {object | undefined} spec - describes customized conversions, if undefined always use 'list'
 * @param {object.<string, object> | undefined} encodings - built-in encode/decode pairs
 * @returns {*}
 */
export const decode = (data, spec = null, encodings = null) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    // scalar item or array
    return !spec || !data || (data.length === 0 && Array.isArray(data))
      ? data
      : decodeColumn(data, null, spec, spec, encodings || ENCODINGS)[1]
  }
  return data['#'] === undefined
    ? decodeItem(data, spec, encodings || ENCODINGS)
    : decodeArray(data, spec, encodings || ENCODINGS)
}

/**
 * decode a list of items from the 'dense columnar format'
 *
 * @param {object} data
 * @param {object | undefined} spec - describes customized conversions, if undefined always use 'list'
 * @param {object.<string, object>} encodings - built-in encode/decode pairs
 * @returns {Array}
 */
const decodeArray = (data, spec, encodings) => {
  const n = data['#']
  const keys = Object.keys(data)
  const result = new Array(n)
  for (let i = 0; i < n; ++i) {
    result[i] = {}
  }
  // for each key, get the value out of the encoded array and
  // put it on that key on the corresponding object in the result array
  for (let i = 0, len = keys.length; i < len; ++i) {
    const k = keys[i]
    const d = data[k]
    if (k !== '#') {
      if (!spec && isKeyed(d)) {
        const values = decode(d)
        for (let j = 0; j < n; ++j) {
          result[j][k] = values && values[j]
        }
      } else {
        const dec = spec ? spec[k] : null
        if (dec !== undefined) {
          const [key, values] = decodeColumn(d, k, dec, spec, encodings, n)
          if (typeof key === 'string') {
            for (let j = 0; j < n; ++j) {
              result[j][key] = values && values[j]
            }
          } else {
            // each item in values is pulled out and splayed into different attrs.
            // [lat, lng], [(1, 3), (2, 4)]
            for (let j = 0; j < n; ++j) {
              const res = result[j]
              const val = values && values[j]
              for (let jj = 0, keyLen = key.length; jj < keyLen; ++jj) {
                res[key[jj]] = val && val[jj]
              }
            }
          }
        }
      }
    }
  }
  return result
}

/**
 * decode one object having attributes that need to be converted
 *
 * @param {any} data
 * @param {object | undefined} spec - describes customized conversions, if undefined always use 'list'
 * @param {object.<string, object>} encodings - built-in encode/decode pairs
 * @returns {any}
 */
const decodeItem = (data, spec, encodings) => {
  const keys = Object.keys(data)
  const result = {}
  // for each key, recursively decode the data for that key
  for (let i = 0, len = keys.length; i < len; ++i) {
    const key = keys[i]
    if (spec) {
      const dec = spec[key]
      if (dec) {
        const typeDef = getTypeDef(dec, encodings, spec)
        const { isObject, isList, encoding, attributes } = typeDef
        if (isObject) {
          result[key] = decode(data[key], dec, encodings)
        } else if (isList) {
          result[key] = decodeColumn(data[key], undefined, encoding, spec, encodings)[1]
        } else if (attributes) {
          for (let j = 0, attributesLen = attributes.length; j < attributesLen; ++j) {
            result[attributes[j]] = data[key][j]
          }
        } else {
          result[key] = data[key]
        }
      }
    } else if (data[key]['#'] !== undefined) {
      result[key] = decodeArray(data[key], spec, encodings)
    } else {
      result[key] = data[key]
    }
  }
  return result
}

/**
 * helper to parse conversion-spec
 * string: eg 'polyline' or a named reference to a previously defined field
 * list-of-1-string: eg ['polyline'] a list of encoded items
 * list-of-string-attrs: eg. ['latlngs', ['lat', 'lng']] splay parameters
 * chain-of-encodings: eg. 'delta|rle' rle-encode the output of delta-encode of data
 * dict: a sub-object specification
 *
 * @param conversion
 * @param {object.<string, object>} encodings - built-in encode/decode pairs
 * @param {object | undefined} libs - describes customized conversions, if undefined always use 'list'
 * @returns {object}
 */
const getTypeDef = (conversion, encodings, libs) => {
  if (typeof conversion === 'string') {
    const enc = encodings[conversion]
    if (enc) {
      return { encoding: conversion }
    } else if (conversion.indexOf('|') > -1) {
      return { encoding: conversion, chain: conversion.split(/ *[|] */) }
    }
    return getTypeDef(libs[conversion], encodings, libs)
  } else if (conversion.length === 1) {
    // lists may contain more complex encodings
    return { encoding: conversion[0], isList: true }
  } else if (conversion.length === 2) {
    // gather/spread may only be used w/ built-in
    if (conversion[0].indexOf('|') > -1) {
      return {
        encoding: conversion[0],
        chain: conversion.split(/ *[|] */),
        attributes: conversion[1],
      }
    }
    return { encoding: conversion[0], attributes: conversion[1] }
  }
  return { encoding: conversion, isObject: true }
}

// returns an encoder. Callable(items), a plain encoding or a chain of encodings
const encoder = (typeDef, encodings) => {
  const { chain, encoding } = typeDef
  if (chain) {
    return (items) => {
      // use each encoding in the chain on the result of the previous one. (reduce)
      let total = items
      for (let i = 0, len = chain.length; i < len; ++i) {
        total = (encodings[chain[i]].encode || encode)(total)
      }
      return total
    }
  }
  return encodings[encoding].encode || encode
}

// returns a decoder. Callable(values), a plain decoding or a chain of decoding
const decoder = (typeDef, encodings) => {
  const { chain, encoding } = typeDef
  if (chain) {
    return (items) => {
      // use each decoding in the chain on the result of the previous one, going backwards. (reduce)
      let total = items
      for (let i = chain.length - 1; i >= 0; --i) {
        total = (encodings[chain[i]].decode || decode)(total)
      }
      return total
    }
  }
  return encodings[encoding].decode || decode
}

const gatherValues = (key, itemData) => {
  // map array of items to array of values of a particular key on each item
  // itemData.map((o) => _keyOf(o, key)) but map is slower than writing the loop
  const values = new Array(itemData.length)
  for (let i = 0, len = itemData.length; i < len; ++i) {
    values[i] = keyOf(itemData[i], key)
  }
  return values
}

const gatherKeys = (keys, item) => {
  const values = new Array(keys.length)
  for (let i = 0, len = keys.length; i < len; ++i) {
    values[i] = keyOf(item, keys[i])
  }
  return values
}

const gatherKeysOf = (keys, items) => {
  const tuples = new Array(items.length)
  for (let i = 0, len = items.length; i < len; ++i) {
    tuples[i] = gatherKeys(keys, items[i])
  }
  return tuples
}

const keysOfItems = (items) => {
  if (items.length === 0) {
    return []
  } else if (items.length === 1) {
    return Object.keys(items[0])
  }
  const keys = {}
  for (let i = 0, len = items.length; i < len; ++i) {
    const item = items[i]
    if (item) {
      const ikeys = Object.keys(item)
      for (let j = 0, lenJ = ikeys.length; j < lenJ; ++j) {
        keys[ikeys[j]] = true
      }
    }
  }
  return Object.keys(keys)
}

/**
 * encode the named values from items, using the named encoder.
 
 * @param items
 * @param name
 * @param conversion
 * @param spec
 * @param encodings
 * @param attrs - a function of (key:String, items:Array) -> Array
 * @returns {any[]|any|*}
 */
const encodeColumn = (items, name, conversion, spec, encodings, attrs) => {
  // encode the named values from items, using the named encoder.
  const typeDef = getTypeDef(conversion, encodings, spec)
  const { attributes, encoding, isList, isObject } = typeDef
  if (attributes) {
    // combine attributes before encoding.
    // we first gather named attributes from the source items, tuple them and pass to the encoder
    return condenseList(encoder(typeDef, encodings)(gatherKeysOf(attributes, items)))
  }
  const values = (attrs || gatherValues)(name, items)
  if (isList) {
    // a list of sub-encoding specs.
    const encoded = new Array(values.length)
    for (let i = 0, len = values.length; i < len; ++i) {
      encoded[i] = encodeColumn(values[i], name, encoding, spec, encodings, kvpValue)
    }
    return encoded
  } else if (isObject) {
    // a dictionary describing how to encode the object at this location
    return encode(values, encoding, encodings)
  } else if (spec) {
    // else: simple named encoding
    return condenseList(encoder(typeDef, encodings)(values))
  }
  return encode(values, null, encodings)
}

/**
 * decode the values, and produce attribute-name(s) and decoded values.
 *
 * @param values
 * @param name
 * @param conversion
 * @param spec
 * @param {object.<string, object>} encodings - built-in encode/decode pairs
 * @param n
 * @returns Array: attributes(s), decoded-value(s)
 */
const decodeColumn = (values, name, conversion, spec, encodings, n) => {
  if (values === null && n > 1) {
    const nulls = new Array(n)
    nulls.fill(null)
    return [name, nulls]
  } else if (conversion === null) {
    return [name, values]
  }
  const typeD = getTypeDef(conversion, encodings, spec)
  const { attributes, isList, encoding, isObject } = typeD
  if (attributes) {
    return [attributes, decoder(typeD, encodings)(values)]
  } else if (isList) {
    const decoded = new Array(values.length)
    for (let i = 0, len = values.length; i < len; ++i) {
      decoded[i] = decodeColumn(values[i], null, encoding, spec, encodings)[1]
    }
    return [name, decoded]
  } else if (isObject) {
    return [name, decode(values, encoding, encodings)]
  }
  // else: simple named encoding
  return [name, decoder(typeD, encodings)(values)]
}
